const UPLOAD_API_BASE = process.env.REACT_APP_UPLOAD_API_BASE || 'https://test-tunnel-b1d9.szapfs.org';

export const DEFAULT_R2_CONTENT_TYPE = 'application/octet-stream';

export const R2_ENDPOINTS = {
  requestUploadUrl: `${UPLOAD_API_BASE}/r2/request-upload-url`,
  confirmUpload: `${UPLOAD_API_BASE}/r2/confirm-upload`
};

const createAbortError = () => {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('Upload aborted', 'AbortError');
  }
  const error = new Error('Upload aborted');
  error.name = 'AbortError';
  return error;
};

const readErrorMessage = async (response, fallback) => {
  try {
    const body = await response.json();
    return body?.error || body?.message || fallback;
  } catch {
    return fallback;
  }
};

async function postJson(url, payload, signal) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok) {
    const message = await readErrorMessage(response, `Request failed: ${response.status}`);
    throw new Error(message);
  }

  return response.json();
}

export function requestR2UploadUrl(payload, signal) {
  return postJson(R2_ENDPOINTS.requestUploadUrl, payload, signal);
}

export function confirmR2Upload(payload, signal) {
  return postJson(R2_ENDPOINTS.confirmUpload, payload, signal);
}

export function putFileToR2({ uploadUrl, file, headers = {}, signal, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const startedAt = performance.now();
    let lastTime = startedAt;
    let lastLoaded = 0;
    let smoothedSpeed = 0;
    let settled = false;

    const settle = (callback, value) => {
      if (settled) return;
      settled = true;
      if (signal) signal.removeEventListener('abort', abortUpload);
      callback(value);
    };

    const abortUpload = () => {
      xhr.abort();
      settle(reject, createAbortError());
    };

    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    if (signal) signal.addEventListener('abort', abortUpload);

    xhr.open('PUT', uploadUrl);

    const requestHeaders = Object.keys(headers).length
      ? headers
      : { 'Content-Type': DEFAULT_R2_CONTENT_TYPE };

    Object.entries(requestHeaders).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        xhr.setRequestHeader(key, value);
      }
    });

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;

      const now = performance.now();
      const totalBytes = event.lengthComputable ? event.total : file.size;
      const loadedBytes = event.loaded;
      const elapsedSeconds = Math.max((now - startedAt) / 1000, 0.001);
      const deltaSeconds = Math.max((now - lastTime) / 1000, 0.001);
      const instantSpeed = Math.max((loadedBytes - lastLoaded) / deltaSeconds, 0);
      const averageSpeed = loadedBytes / elapsedSeconds;

      smoothedSpeed = smoothedSpeed
        ? smoothedSpeed * 0.75 + instantSpeed * 0.25
        : instantSpeed || averageSpeed;

      const remainingBytes = Math.max(totalBytes - loadedBytes, 0);
      const etaSeconds = smoothedSpeed > 0 && remainingBytes > 0
        ? remainingBytes / smoothedSpeed
        : null;

      onProgress({
        loadedBytes,
        totalBytes,
        percent: totalBytes > 0 ? (loadedBytes / totalBytes) * 100 : 0,
        speedBytesPerSecond: smoothedSpeed,
        etaSeconds
      });

      lastTime = now;
      lastLoaded = loadedBytes;
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress && file.size) {
          onProgress({
            loadedBytes: file.size,
            totalBytes: file.size,
            percent: 100,
            speedBytesPerSecond: smoothedSpeed,
            etaSeconds: null
          });
        }
        settle(resolve, {
          status: xhr.status,
          etag: xhr.getResponseHeader('ETag')
        });
      } else {
        settle(reject, new Error(`R2 PUT failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => settle(reject, new Error('R2 PUT network error'));
    xhr.onabort = () => settle(reject, createAbortError());
    xhr.send(file);
  });
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

export function formatBytesPerSecond(bytesPerSecond) {
  return `${formatBytes(bytesPerSecond)}/s`;
}

export function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '--';
  const rounded = Math.ceil(seconds);
  const mins = Math.floor(rounded / 60);
  const secs = rounded % 60;
  if (mins <= 0) return `${secs}s`;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m`;
}
